/**
 * TermWebDock — Term/Phrase web curation for human-like specification
 * generation (docs/SPEC_TERM_WEB_DESIGN.md).
 *
 * HI mode (default): "Extract" runs spec_term_extract and lands rows
 * as suggestions grouped claim-wise; the drafter accepts/rejects each,
 * adds manual terms, then "Generate" fires the chained 6-sentence
 * paragraph pass into the Detailed Description.
 *
 * Auto mode: the "auto-select" checkbox makes Extract pre-select
 * everything, so Extract → Generate is the full pipeline with no
 * curation stop.
 *
 * Reordering: ▲/▼ buttons rewrite the web order (the order IS the
 * narrative spine — each term's S3/S4 sentences connect to the terms
 * before it).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Network, Loader2, Check, X, Plus, ArrowUp, ArrowDown,
  Sparkles, RefreshCw,
} from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import {
  postSpecTermsExtract, postSpecTermCreate, patchSpecTerm,
  postSpecTermsReorder, postSpecTermsGenerate,
  type SpecTermWebEntry,
} from '@/api/client';
import Dock from './Dock';

import { apiErrorText } from '../../lib/describeApiError';
export default function TermWebDock(): JSX.Element {
  const matterId = useSpecStore((s) => s.matterId);
  const termWeb = useSpecStore((s) => s.termWeb);
  const termWebStatus = useSpecStore((s) => s.termWebStatus);
  const loadTermWeb = useSpecStore((s) => s.loadTermWeb);
  const showToast = useSpecStore((s) => s.showToast);

  const [extracting, setExtracting] = useState(false);
  const [autoSelect, setAutoSelect] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busyTermId, setBusyTermId] = useState<string | null>(null);
  const [newTermText, setNewTermText] = useState('');
  const [newTermKind, setNewTermKind] = useState<'term' | 'phrase'>('term');

  useEffect(() => {
    void loadTermWeb();
  }, [loadTermWeb]);

  const selectedCount = useMemo(
    () => termWeb.filter((t) => t.status === 'selected').length,
    [termWeb],
  );
  const ordered = useMemo(
    () => [...termWeb].sort((a, b) => a.sequence_num - b.sequence_num),
    [termWeb],
  );

  const onExtract = useCallback(async () => {
    if (!matterId || extracting) return;
    setExtracting(true);
    try {
      const r = await postSpecTermsExtract(matterId, autoSelect);
      await loadTermWeb(true);
      showToast(
        `Extracted ${r.inserted} new term${r.inserted === 1 ? '' : 's'} `
        + `(${r.total} total, ${r.mode === 'auto' ? 'auto-selected' : 'awaiting curation'}).`,
      );
    } catch (e: any) {
      showToast(
        e?.response?.data?.detail?.detail
        || apiErrorText(e, 'Term extraction failed.'),
      );
    } finally {
      setExtracting(false);
    }
  }, [matterId, extracting, autoSelect, loadTermWeb, showToast]);

  const onSetStatus = useCallback(
    async (t: SpecTermWebEntry, status: 'selected' | 'rejected' | 'suggested') => {
      if (!matterId || busyTermId) return;
      setBusyTermId(t.id);
      try {
        await patchSpecTerm(matterId, t.id, { status });
        await loadTermWeb(true);
      } catch {
        showToast('Update failed.');
      } finally {
        setBusyTermId(null);
      }
    },
    [matterId, busyTermId, loadTermWeb, showToast],
  );

  const onMove = useCallback(
    async (t: SpecTermWebEntry, dir: -1 | 1) => {
      if (!matterId || busyTermId) return;
      const idx = ordered.findIndex((x) => x.id === t.id);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= ordered.length) return;
      const ids = ordered.map((x) => x.id);
      [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
      setBusyTermId(t.id);
      try {
        await postSpecTermsReorder(matterId, ids);
        await loadTermWeb(true);
      } catch {
        showToast('Reorder failed.');
      } finally {
        setBusyTermId(null);
      }
    },
    [matterId, busyTermId, ordered, loadTermWeb, showToast],
  );

  const onAdd = useCallback(async () => {
    const text = newTermText.trim();
    if (!matterId || !text) return;
    try {
      await postSpecTermCreate(matterId, { term_text: text, term_kind: newTermKind });
      setNewTermText('');
      await loadTermWeb(true);
    } catch (e: any) {
      showToast(
        e?.response?.data?.detail?.detail || 'Add failed (duplicate term?).',
      );
    }
  }, [matterId, newTermText, newTermKind, loadTermWeb, showToast]);

  const onGenerate = useCallback(async () => {
    if (!matterId || generating || selectedCount === 0) return;
    setGenerating(true);
    try {
      const r = await postSpecTermsGenerate(matterId, { overwrite: true });
      await loadTermWeb(true);
      showToast(
        `Generated ${r.paragraph_count} paragraph${r.paragraph_count === 1 ? '' : 's'} `
        + `from ${r.terms_generated} terms in ${r.batches} batch${r.batches === 1 ? '' : 'es'}.`,
      );
    } catch (e: any) {
      showToast(
        e?.response?.data?.detail?.detail
        || apiErrorText(e, 'Generation failed.'),
      );
    } finally {
      setGenerating(false);
    }
  }, [matterId, generating, selectedCount, loadTermWeb, showToast]);

  const statusChip = (t: SpecTermWebEntry) => {
    if (t.status === 'selected') {
      return 'border-green-300 bg-green-50 text-green-800';
    }
    if (t.status === 'rejected') {
      return 'border-zinc-200 bg-zinc-50 text-zinc-400 line-through';
    }
    return 'border-amber-200 bg-amber-50/50 text-zinc-700';
  };

  return (
    <Dock
      dockKey="termWeb"
      icon={<Network className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Term web"
      pillCount={termWeb.length}
      meta={
        termWeb.length === 0
          ? (termWebStatus === 'loading'
              ? 'Loading…'
              : 'Extract Terms & Phrases from the frozen claims to start the web.')
          : `${selectedCount} selected · order drives the Detailed Description narrative.`
      }
      testId="spec-dock-term-web"
    >
      {/* Actions row */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="term-web-extract"
          onClick={onExtract}
          disabled={extracting || generating}
          className="flex items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        >
          {extracting
            ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            : <RefreshCw className="h-3 w-3" aria-hidden />}
          Extract
        </button>
        <label className="flex items-center gap-1 text-[10px] text-zinc-500 select-none">
          <input
            type="checkbox"
            checked={autoSelect}
            onChange={(e) => setAutoSelect(e.target.checked)}
            data-testid="term-web-auto-select"
            className="h-3 w-3"
          />
          auto-select (Auto mode)
        </label>
        <button
          type="button"
          data-testid="term-web-generate"
          onClick={onGenerate}
          disabled={generating || extracting || selectedCount === 0}
          title={selectedCount === 0
            ? 'Select at least one term first'
            : `Generate ${selectedCount} chained 6-sentence paragraphs into the Detailed Description`}
          className="flex items-center gap-1 rounded-sm bg-[#002FA7] px-2 py-1 text-[11px] font-medium text-white hover:bg-[#002277] disabled:opacity-50"
        >
          {generating
            ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            : <Sparkles className="h-3 w-3" aria-hidden />}
          Generate
        </button>
      </div>

      {/* Term list, web order */}
      {ordered.length > 0 && (
        <ul className="space-y-1" data-testid="term-web-list">
          {ordered.map((t, i) => (
            <li
              key={t.id}
              data-testid={`term-web-row-${t.sequence_num}`}
              className={`rounded-sm border px-2 py-1.5 text-[11px] ${statusChip(t)}`}
            >
              <div className="flex items-start gap-1.5">
                <span className="mt-0.5 shrink-0 font-mono text-[9px] text-zinc-400">
                  {i + 1}
                </span>
                <span className={`mt-0.5 shrink-0 rounded-sm border px-1 font-mono text-[9px] uppercase ${
                  t.term_kind === 'term'
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-purple-200 bg-purple-50 text-purple-700'
                }`}>
                  {t.term_kind}
                </span>
                <span className="min-w-0 flex-1 break-words leading-snug">
                  {t.term_text}
                  {t.source_claim_numbers.length > 0 && (
                    <span className="ml-1 text-[9px] text-zinc-400">
                      (cl. {t.source_claim_numbers.join(', ')})
                    </span>
                  )}
                  {t.paragraph_id && (
                    <span className="ml-1 text-[9px] text-green-600">¶</span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-0.5">
                  {busyTermId === t.id ? (
                    <Loader2 className="h-3 w-3 animate-spin text-zinc-400" aria-hidden />
                  ) : (
                    <>
                      {t.status !== 'selected' && (
                        <button
                          type="button"
                          title="Accept"
                          data-testid={`term-web-accept-${t.sequence_num}`}
                          onClick={() => void onSetStatus(t, 'selected')}
                          className="rounded p-0.5 text-green-700 hover:bg-green-100"
                        >
                          <Check className="h-3 w-3" aria-hidden />
                        </button>
                      )}
                      {t.status !== 'rejected' && (
                        <button
                          type="button"
                          title="Reject"
                          data-testid={`term-web-reject-${t.sequence_num}`}
                          onClick={() => void onSetStatus(t, 'rejected')}
                          className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-red-600"
                        >
                          <X className="h-3 w-3" aria-hidden />
                        </button>
                      )}
                      <button
                        type="button"
                        title="Move up"
                        onClick={() => void onMove(t, -1)}
                        disabled={i === 0}
                        className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 disabled:opacity-30"
                      >
                        <ArrowUp className="h-3 w-3" aria-hidden />
                      </button>
                      <button
                        type="button"
                        title="Move down"
                        onClick={() => void onMove(t, 1)}
                        disabled={i === ordered.length - 1}
                        className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 disabled:opacity-30"
                      >
                        <ArrowDown className="h-3 w-3" aria-hidden />
                      </button>
                    </>
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Manual add */}
      <div className="mt-2 flex items-center gap-1">
        <select
          value={newTermKind}
          onChange={(e) => setNewTermKind(e.target.value as 'term' | 'phrase')}
          data-testid="term-web-add-kind"
          className="rounded-sm border border-zinc-300 bg-white px-1 py-1 text-[10px] text-zinc-700"
        >
          <option value="term">term</option>
          <option value="phrase">phrase</option>
        </select>
        <input
          type="text"
          value={newTermText}
          onChange={(e) => setNewTermText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void onAdd(); }}
          placeholder="Add a term or phrase…"
          data-testid="term-web-add-input"
          className="min-w-0 flex-1 rounded-sm border border-zinc-300 bg-white px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
        />
        <button
          type="button"
          data-testid="term-web-add-submit"
          onClick={() => void onAdd()}
          disabled={!newTermText.trim()}
          className="rounded-sm border border-zinc-300 bg-white p-1 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
        >
          <Plus className="h-3 w-3" aria-hidden />
        </button>
      </div>
    </Dock>
  );
}
